import * as React from 'react'
import { FC, useMemo } from 'react'
import { SearchField } from '@sales-center/components'
import { useDebounce } from '@user/utils/useDebounce'
import { useContactSearch } from '@user/query/useContactSearch'
import { MatchedContactCardZero } from './MatchedContactCardZero'
import { MatchedContactCard } from './MatchedContactCard'
import { ILeadDetail } from '@user/store/lead'
import { IOpportunityDetail } from '@user/store/opportunity'
import { useContactAlreadyInUse } from '../hooks/useContactAlreadyInUse'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import CircularProgress from '@mui/material/CircularProgress'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { Theme } from '@mui/material/styles'

interface Props {
  detail: ILeadDetail | IOpportunityDetail
  onContactSet: (contactId: number) => void
  onSearchTextChange: (text: string) => void
  onSwitchToCreate: () => void
  searchText: string
}

export const MatchContact: FC<Props> = (props: Props) => {
  const {
    onContactSet,
    onSearchTextChange,
    onSwitchToCreate,
    searchText,
    detail,
    detail: {
      secondaryContactsSummary: { externalContacts },
    },
  } = props

  const debouncedSearchText = useDebounce(searchText)

  const { isLoading: searchResultsLoading, data: searchResults } =
    useContactSearch(debouncedSearchText, 1, 50)

  const contactAlreadyInUse = useContactAlreadyInUse(detail)

  const filteredResults = useMemo(
    () =>
      searchResults?.items.filter(
        (x) => !externalContacts.some((o) => o.id === x.id),
      ),
    [externalContacts, searchResults],
  )

  return (
    <Box>
      <Box display="flex" mb={2} alignItems="center">
        <Box mr={2}>
          {!searchResultsLoading && !!filteredResults && (
            <Typography>
              {filteredResults.length} Possible{' '}
              {filteredResults.length === 1 ? 'Match' : 'Matches'} Found
            </Typography>
          )}
        </Box>
        <Box flexGrow={1}>
          <SearchField
            onChange={onSearchTextChange}
            placeHolderText="Search"
            delayTimeMS={500}
            text={searchText}
          />
        </Box>
      </Box>

      {/* ========== CARDS ========== */}
      <Paper elevation={0}>
        {searchResultsLoading ? (
          <Box
            sx={{
              paddingTop: (theme: Theme) => theme.spacing(4.8),
              textAlign: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <List disablePadding>
            {!!filteredResults && filteredResults.length > 0 ? (
              filteredResults.map((model, i) => (
                <ListItem key={i} disableGutters>
                  <MatchedContactCard
                    contact={model}
                    disabled={contactAlreadyInUse(model.id)}
                    disabledReason="This contact has already been added."
                    onAddContactClick={onContactSet}
                  />
                </ListItem>
              ))
            ) : (
              <MatchedContactCardZero
                onAddNewClick={onSwitchToCreate}
                searchText={debouncedSearchText}
              />
            )}
          </List>
        )}
      </Paper>
    </Box>
  )
}
MatchContact.displayName = 'MatchContact'
